import React, { ReactNode, useState } from 'react';
import { Grid, InputAdornment, SxProps, TextField, Theme, useMediaQuery, useTheme } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';
import { TableComponent } from './TableComponent';
import { mb } from '../common/theme/mbTokens';

export type CellProps<TEntity> = {
  id: string;
  alignment: 'left' | 'center' | 'right' | 'justify' | 'inherit' | undefined;
  label?: ReactNode;
  render?: (entity: TEntity) => any;
  width?: number;
  hideCell?: boolean;
  noPadding?: boolean;
  sortKey?: string;
};

export type TableSortState = { sortBy: string; sortDirection: 'asc' | 'desc' };

type FetcherFunction<T> = (params: {
  search?: string;
  page: number;
  perPage: number;
  filterState?: object;
  sort?: TableSortState;
}) => Promise<T[]>;

interface UseTableOptions<T> {
  defaultRowsPerPage?: number;
  fetcher: FetcherFunction<T>;
  opId: string;
  actionsComponent?: ReactNode;
  tableDetails: CellProps<T>[];
  hidePaging?: boolean;
  revalidateOnFocus?: boolean;
  hideSearch?: boolean;
  filterState?: object;
  initialSearchValue?: string;
  getRowSx?: (item: T) => SxProps<Theme> | undefined;
  splitColumnsOnWideScreen?: boolean;
  /** Full-page tables: stretch to the parent's height so the pager sits at the bottom. */
  fillHeight?: boolean;
  /** The table grows with its rows and the page around it scrolls: no vertical scroll of its own. */
  scrollWithPage?: boolean;
}

interface UseTableResult<T> {
  data: T[];
  mutate: KeyedMutator<T[]>;
  TableComponent: JSX.Element;
  isLoadingData: boolean;
}

export function useTable<T>({
  defaultRowsPerPage = 25,
  opId,
  fetcher,
  tableDetails,
  actionsComponent,
  hidePaging,
  hideSearch,
  revalidateOnFocus = true,
  filterState,
  initialSearchValue,
  getRowSx,
  splitColumnsOnWideScreen,
  fillHeight = false,
  scrollWithPage = false,
}: UseTableOptions<T>): UseTableResult<T> {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchValue, setSearchValue] = useState<string>(initialSearchValue ?? '');
  const [sort, setSort] = useState<TableSortState | undefined>(undefined);
  const [debouncedSearchValue] = useDebounce(searchValue, isSmallScreen ? 1500 : 500);
  const [debouncedFilterState] = useDebounce(filterState, isSmallScreen ? 1500 : 500);

  // Part of the SWR key, so changing the sort refetches from the server instead of reordering
  // whatever happens to be cached.
  const { data, mutate, isLoading } = useSWR(
    {
      search: debouncedSearchValue,
      page: page + 1,
      perPage: rowsPerPage,
      opId,
      filterState: debouncedFilterState,
      sort,
    },
    fetcher,
    { refreshInterval: 20000, revalidateOnFocus },
  );

  const toggleSort = (sortBy: string) => {
    setPage(0);
    setSort((previous) =>
      previous?.sortBy === sortBy && previous.sortDirection === 'asc'
        ? { sortBy, sortDirection: 'desc' }
        : { sortBy, sortDirection: 'asc' },
    );
  };

  const renderTable = () => (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        sx={{ px: mb.layout.cardPadding, pt: mb.layout.cardPadding, pb: 1.5 }}>
        {!hideSearch && (
          <Grid item>
            <TextField
              value={searchValue}
              variant="outlined"
              className="search-input"
              placeholder={t('search')}
              onChange={(e) => {
                setPage(0);
                setSearchValue(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        <Grid item>{actionsComponent}</Grid>
      </Grid>
      <Grid container flexDirection="column" flexWrap="nowrap" sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
        <TableComponent
          tableDetails={tableDetails}
          hidePaging={hidePaging}
          page={page}
          rowsPerPage={rowsPerPage}
          isLoading={isLoading}
          setRowsPerPage={setRowsPerPage}
          data={data}
          setPage={setPage}
          getRowSx={getRowSx}
          splitColumnsOnWideScreen={splitColumnsOnWideScreen}
          sort={sort}
          onToggleSort={toggleSort}
          fillHeight={fillHeight}
          scrollWithPage={scrollWithPage}
        />
      </Grid>
    </Grid>
  );

  return {
    data: data || [],
    mutate,
    TableComponent: renderTable(),
    isLoadingData: isLoading,
  };
}
